/**
 * AkteSections — Prozessschritt-Sections für Kfz-Gutachten
 *
 * Eine Datei, mehrere Exports — eine für jede Prozessphase.
 * Alle Sections lesen WinValue-synchronisierte Daten aus dem `akte`-Objekt
 * und zeigen sie im klaren Bürolayout (ähnlich WinValue UltraExpert).
 *
 * Wiederverwendete Primitiven:
 *   - Card, InfoRow, SectionHeader, ValueBlock, EmptyHint
 *
 * Verwendung: Wird von AkteDetail.tsx anstelle des Platzhalters
 *   "Dieser Bereich wird noch entwickelt" gerendert.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useHonorarvereinbarungTemplates } from '@/hooks/useHonorarvereinbarungTemplates';
import {
  ClipboardList, Users, Car, Eye, Camera, BarChart3, TrendingDown, ArrowDownRight,
  Receipt, ScrollText, FileText, Phone, Mail, MapPin, AlertTriangle,
  Download, RefreshCw, Loader2, ExternalLink, Image as ImageIcon, Info,
  CheckCircle2, Calendar, Shield, Gauge, Hash, User, Building2, Scale, Wrench,
  Pencil, Save, X, Upload, ArrowUpDown, SortAsc, SortDesc, Plus,
} from 'lucide-react';
import {
  StellungnahmeBadge,
  hasStellungnahmeLabel,
} from '@/components/akte/StellungnahmeLabel';
import type { Akte } from '@/lib/database.types';

// ─── Typen ────────────────────────────────────────────────────────────────────

interface SectionProps {
  akte: Akte;
  onChanged?: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function formatEuro(val: number | null | undefined): string {
  if (val == null || Number.isNaN(Number(val))) return '—';
  return Number(val).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

export function formatDate(val: string | null | undefined): string {
  if (!val) return '—';
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return val;
    return d.toLocaleDateString('de-DE');
  } catch {
    return val;
  }
}

function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Primitiven ──────────────────────────────────────────────────────────────

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white rounded-xl border border-gray-200 p-4 sm:p-5 ${className}`}>
      {children}
    </div>
  );
}

function SectionHeader({ icon: Icon, title, hint, action }: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  hint?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-blue-600" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-[15px] font-bold text-gray-900">{title}</h3>
        {hint && <p className="text-[12px] text-gray-500 mt-0.5">{hint}</p>}
      </div>
      {action}
    </div>
  );
}

function InfoRow({ label, value, mono, wide }: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  wide?: boolean;
}) {
  return (
    <div className={`flex items-start gap-3 ${wide ? 'sm:col-span-2' : ''}`}>
      <span className="text-[12px] text-gray-400 w-32 sm:w-36 flex-shrink-0 pt-0.5">{label}</span>
      <span className={`text-[13px] font-medium text-gray-800 break-words min-w-0 ${mono ? 'font-mono' : ''}`}>
        {value ?? '—'}
      </span>
    </div>
  );
}

function FieldGrid({ children, cols = 2 }: { children: React.ReactNode; cols?: 1 | 2 | 3 }) {
  const g = cols === 1 ? 'grid-cols-1' : cols === 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2';
  return <div className={`grid ${g} gap-3`}>{children}</div>;
}

function EmptyHint({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
      <Info className="w-4 h-4 text-blue-600 flex-shrink-0" />
      <p className="text-[12px] text-blue-700">{text}</p>
    </div>
  );
}

function SyncBadge({ akte }: { akte: Akte }) {
  if (!akte.winvalue_uid) return null;
  const ts = akte.winvalue_last_sync ? new Date(akte.winvalue_last_sync) : null;
  return (
    <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] text-emerald-700 font-medium">
      <RefreshCw className="w-3 h-3" />
      <span>WinValue</span>
      {ts && (
        <span className="text-emerald-500 font-normal">
          {ts.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
        </span>
      )}
    </div>
  );
}

function CostValue({ label, value, highlight }: { label: string; value: number | null | undefined; highlight?: boolean }) {
  return (
    <div className={`rounded-lg border p-3 ${highlight ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
      <p className="text-[11px] text-gray-500">{label}</p>
      <p className={`text-[16px] font-bold font-mono mt-1 ${highlight ? 'text-blue-700' : 'text-gray-900'}`}>
        {formatEuro(value)}
      </p>
    </div>
  );
}

// ─── Shared Edit Helpers ─────────────────────────────────────────────────────

function EditButton({ editing, saving, onEdit, onSave, onCancel }: {
  editing: boolean; saving: boolean;
  onEdit: () => void; onSave: () => void; onCancel: () => void;
}) {
  if (!editing) {
    return (
      <button onClick={onEdit} className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
        <Pencil className="w-3.5 h-3.5" /> Bearbeiten
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <button onClick={onCancel} className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium text-gray-500 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
        <X className="w-3.5 h-3.5" /> Abbrechen
      </button>
      <button onClick={onSave} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Speichern
      </button>
    </div>
  );
}

function EditField({ label, value, type = 'text', onChange, options, wide }: {
  label: string; value: string; type?: 'text' | 'date' | 'select' | 'textarea';
  onChange: (v: string) => void; options?: { value: string; label: string }[]; wide?: boolean;
}) {
  return (
    <div className={wide ? 'sm:col-span-2' : ''}>
      <label className="text-[12px] text-gray-400 block mb-1">{label}</label>
      {type === 'textarea' ? (
        <textarea value={value} onChange={e => onChange(e.target.value)} rows={2}
          className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none" />
      ) : type === 'select' ? (
        <select value={value} onChange={e => onChange(e.target.value)}
          className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
          <option value="">— Auswählen —</option>
          {options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input type={type} value={value} onChange={e => onChange(e.target.value)}
          className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
      )}
    </div>
  );
}

function useEditableSection(akte: Akte, keys: string[], onChanged?: () => void) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});

  const startEditing = useCallback(() => {
    const data: Record<string, string> = {};
    const aktePartial = akte as Partial<Record<keyof Akte, unknown>>;
    keys.forEach(k => {
      const val = aktePartial[k as keyof Akte];
      data[k] = typeof val === 'boolean' ? (val ? 'true' : '') : (val != null ? String(val) : '');
    });
    setForm(data);
    setEditing(true);
  }, [akte, keys]);

  const cancel = () => { setEditing(false); setForm({}); };

  const save = async () => {
    setSaving(true);
    try {
      const updates: Record<string, any> = {};
      keys.forEach(k => { updates[k] = form[k] === '' ? null : form[k]; });
      const { error } = await supabase.from('akten').update(updates).eq('id', akte.id);
      if (error) throw error;
      toast.success('Gespeichert');
      setEditing(false);
      onChanged?.();
    } catch (err: any) {
      toast.error('Fehler beim Speichern', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const set = (key: string, val: string) => setForm(prev => ({ ...prev, [key]: val }));

  return { editing, saving, form, startEditing, cancel, save, set };
}

// ═══════════════════════════════════════════════════════════════════════════
// 1) AUFTRAG — Auftragsdaten (Auftragsart, Schadendatum/ort, Erteilt durch)
// ═══════════════════════════════════════════════════════════════════════════

const AUFTRAG_KEYS = [
  'schadenart', 'auftragsdatum', 'status', 'erteilt_durch',
  'schadentag', 'schadennummer', 'schadenort', 'beschreibung',
  'besichtigungsdatum', 'besichtigt_durch',
];

const STATUS_OPTIONS = [
  { value: 'offen', label: 'Offen' },
  { value: 'in_bearbeitung', label: 'In Bearbeitung' },
  { value: 'abgeschlossen', label: 'Abgeschlossen' },
  { value: 'neu', label: 'Neu' },
  { value: 'warten', label: 'Warten' },
  { value: 'storniert', label: 'Storniert' },
];

const SCHADENART_OPTIONS = [
  { value: 'Haftpflicht', label: 'Haftpflicht' },
  { value: 'Kasko', label: 'Kasko' },
  { value: 'Kurzgutachten', label: 'Kurzgutachten' },
  { value: 'Beweissicherung', label: 'Beweissicherung' },
  { value: 'Wertgutachten', label: 'Wertgutachten' },
];

// ─── Honorarvereinbarung-Selektor (Issue #373) ──────────────────────────────
// Dropdown, über den die zu verwendende Honorarvereinbarungs-Vorlage (BVSK,
// HUK, individuell …) pro Akte ausgewählt wird. Schreibt direkt auf
// `akten.honorarvereinbarung_template_id` und ruft `onChanged` zum Reload auf.
function HonorarvereinbarungCard({ akte, onChanged }: SectionProps) {
  const { data: templates, loading } = useHonorarvereinbarungTemplates();
  const [saving, setSaving] = useState(false);
  const selectedId: string | null = akte.honorarvereinbarung_template_id ?? null;

  const handleChange = async (next: string) => {
    const value = next === '' ? null : next;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('akten')
        .update({ honorarvereinbarung_template_id: value })
        .eq('id', akte.id);
      if (error) throw error;
      toast.success('Honorarvereinbarung gespeichert');
      onChanged?.();
    } catch (err: any) {
      toast.error('Fehler beim Speichern', { description: err?.message ?? String(err) });
    } finally {
      setSaving(false);
    }
  };

  const selected = templates.find((t) => t.id === selectedId) ?? null;

  return (
    <Card>
      <SectionHeader
        icon={Scale}
        title="Honorarvereinbarung"
        hint="Welche Vorlage wird für diese Akte verwendet?"
      />
      <div className="space-y-2">
        <label className="text-[12px] text-gray-400 block" htmlFor="honorarvereinbarung-template">
          Vorlage
        </label>
        <select
          id="honorarvereinbarung-template"
          aria-label="Honorarvereinbarung-Vorlage"
          value={selectedId ?? ''}
          disabled={loading || saving}
          onChange={(ev) => handleChange(ev.target.value)}
          className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:opacity-60"
        >
          <option value="">— keine Vorlage gewählt —</option>
          {templates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
        {selected?.beschreibung && (
          <p className="text-[12px] text-gray-500">{selected.beschreibung}</p>
        )}
        {saving && (
          <p className="text-[12px] text-gray-400 flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> Speichert…
          </p>
        )}
      </div>
    </Card>
  );
}

// ─── Honorar-Basis-Version-Picker (Issue #371) ──────────────────────────────
// Tab-Auswahl der Tarif-Version pro Akte: BVSK 2026 (Standard), BVSK 2024
// (Fallback bei Streit-Verfahren), HUK. Schreibt auf akten.honorar_basis_version.
type HonorarBasisVersion = 'BVSK_2026' | 'BVSK_2024' | 'HUK' | 'custom';

const HONORAR_BASIS_TABS: Array<{ value: HonorarBasisVersion; label: string; hint: string }> = [
  { value: 'BVSK_2026', label: 'BVSK 2026', hint: 'Standard ab 2026' },
  { value: 'BVSK_2024', label: 'BVSK 2024', hint: 'Fallback bei Streit' },
  { value: 'HUK', label: 'HUK', hint: 'HUK-Tarif' },
];

function HonorarBasisVersionCard({ akte, onChanged }: SectionProps) {
  const [saving, setSaving] = useState(false);
  const current: HonorarBasisVersion = (akte.honorar_basis_version as HonorarBasisVersion) ?? 'BVSK_2026';

  const handleChange = async (next: HonorarBasisVersion) => {
    if (next === current) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('akten')
        .update({ honorar_basis_version: next })
        .eq('id', akte.id);
      if (error) throw error;
      toast.success('Honorar-Basis gespeichert');
      onChanged?.();
    } catch (err: any) {
      toast.error('Fehler beim Speichern', { description: err?.message ?? String(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <SectionHeader
        icon={Scale}
        title="Honorar-Basis"
        hint="Tarif-Version für diese Akte"
      />
      <div className="flex gap-2 flex-wrap">
        {HONORAR_BASIS_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            disabled={saving}
            onClick={() => handleChange(tab.value)}
            title={tab.hint}
            className={`px-4 py-2 rounded-lg text-[13px] font-semibold border transition-colors disabled:opacity-60 ${
              current === tab.value
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {saving && (
        <p className="text-[12px] text-gray-400 flex items-center gap-1 mt-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Speichert…
        </p>
      )}
      {current === 'BVSK_2024' && (
        <p className="text-[12px] text-amber-600 mt-2 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          Fallback BVSK 2024 aktiv — für Streit-Verfahren mit Versicherern.
        </p>
      )}
    </Card>
  );
}

export function AuftragSection({ akte, onChanged }: SectionProps) {
  const e = useEditableSection(akte, AUFTRAG_KEYS, onChanged);

  return (
    <div className="space-y-4">
      <Card>
        <SectionHeader icon={ClipboardList} title="Auftrag" hint="Auftragsart und -eingang"
          action={<div className="flex items-center gap-2"><SyncBadge akte={akte} /><EditButton editing={e.editing} saving={e.saving} onEdit={e.startEditing} onSave={e.save} onCancel={e.cancel} /></div>} />
        {e.editing ? (
          <FieldGrid cols={2}>
            <EditField label="Auftragsart" value={e.form.schadenart} type="select" options={SCHADENART_OPTIONS} onChange={v => e.set('schadenart', v)} />
            <EditField label="Auftragsdatum" value={e.form.auftragsdatum} type="date" onChange={v => e.set('auftragsdatum', v)} />
            <EditField label="Status" value={e.form.status} type="select" options={STATUS_OPTIONS} onChange={v => e.set('status', v)} />
            <EditField label="Erteilt durch" value={e.form.erteilt_durch} onChange={v => e.set('erteilt_durch', v)} />
          </FieldGrid>
        ) : (
          <FieldGrid cols={2}>
            <InfoRow label="Aktenzeichen" value={akte.akten_nr} mono />
            <InfoRow label="Auftragsart" value={akte.schadenart} />
            <InfoRow label="Gutachtentyp" value={akte.auftragstyp_sv === 'kasko' ? 'Kaskogutachten' : akte.schadenart === 'Haftpflicht' ? 'Haftpflichtgutachten' : akte.schadenart} />
            <InfoRow label="Auftragsdatum" value={formatDate(akte.auftragsdatum)} />
            <InfoRow label="Status" value={akte.status} />
            <InfoRow label="Erteilt durch" value={akte.erteilt_durch || akte.anspruchsteller_name} />
          </FieldGrid>
        )}
      </Card>

      <HonorarvereinbarungCard akte={akte} onChanged={onChanged} />

      <HonorarBasisVersionCard akte={akte} onChanged={onChanged} />

      <Card>
        <SectionHeader icon={AlertTriangle} title="Schadendaten" hint="Was ist passiert, wann und wo" />
        {e.editing ? (
          <FieldGrid cols={2}>
            <EditField label="Schadendatum" value={e.form.schadentag} type="date" onChange={v => e.set('schadentag', v)} />
            <EditField label="Schadennummer" value={e.form.schadennummer} onChange={v => e.set('schadennummer', v)} />
            <EditField label="Schadenort" value={e.form.schadenort} onChange={v => e.set('schadenort', v)} wide />
            <EditField label="Beschreibung" value={e.form.beschreibung} type="textarea" onChange={v => e.set('beschreibung', v)} wide />
          </FieldGrid>
        ) : (
          <FieldGrid cols={2}>
            <InfoRow label="Schadendatum" value={formatDate(akte.schadentag || akte.schadendatum)} />
            <InfoRow label="Schadennummer" value={akte.schadennummer} mono />
            <InfoRow label="Schadenort" value={akte.schadenort} wide />
            <InfoRow label="Beschreibung" value={akte.beschreibung} wide />
          </FieldGrid>
        )}
      </Card>

      <Card>
        <SectionHeader icon={Eye} title="Besichtigung" hint="Erster Termin vor Ort" />
        {e.editing ? (
          <FieldGrid cols={2}>
            <EditField label="Besichtigungsdatum" value={e.form.besichtigungsdatum} type="date" onChange={v => e.set('besichtigungsdatum', v)} />
            <EditField label="Besichtigt durch" value={e.form.besichtigt_durch} onChange={v => e.set('besichtigt_durch', v)} />
          </FieldGrid>
        ) : (
          <FieldGrid cols={2}>
            <InfoRow label="Besichtigungsdatum" value={formatDate(akte.besichtigungsdatum)} />
            <InfoRow label="Besichtigt durch" value={akte.besichtigt_durch || 'Andreas Koop'} />
          </FieldGrid>
        )}
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 2) BETEILIGTE — Alle Rollen (Anspruchsteller, Versicherung, Gegner, Anwalt, Werkstatt)
// ═══════════════════════════════════════════════════════════════════════════

const ROLE_META: Array<{
  code: string;
  label: string;
  bg: string;
  text: string;
  fields: { name: keyof Akte; strasse?: keyof Akte; plz?: keyof Akte; ort?: keyof Akte; telefon?: keyof Akte; email?: keyof Akte };
}> = [
  { code: 'AS', label: 'Anspruchsteller', bg: 'bg-emerald-100', text: 'text-emerald-800',
    fields: { name: 'anspruchsteller_name', strasse: 'anspruchsteller_strasse', plz: 'anspruchsteller_plz', ort: 'anspruchsteller_ort', telefon: 'anspruchsteller_telefon', email: 'anspruchsteller_email' } },
  { code: 'VN', label: 'Versicherung', bg: 'bg-blue-100', text: 'text-blue-800',
    fields: { name: 'versicherung', strasse: 'versicherung_adresse', plz: 'versicherung_plz', ort: 'versicherung_ort', telefon: 'versicherung_telefon', email: 'versicherung_email' } },
  { code: 'UG', label: 'Unfallgegner', bg: 'bg-red-100', text: 'text-red-800',
    fields: { name: 'gegner_name', strasse: 'gegner_adresse', plz: 'gegner_plz', ort: 'gegner_ort' } },
  { code: 'RA', label: 'Rechtsanwalt', bg: 'bg-indigo-100', text: 'text-indigo-800',
    fields: { name: 'anwalt_name', strasse: 'anwalt_adresse', plz: 'anwalt_plz', ort: 'anwalt_ort', telefon: 'anwalt_telefon', email: 'anwalt_email' } },
  { code: 'WS', label: 'Werkstatt', bg: 'bg-amber-100', text: 'text-amber-800',
    fields: { name: 'werkstatt_name', strasse: 'werkstatt_adresse', plz: 'werkstatt_plz', ort: 'werkstatt_ort', telefon: 'werkstatt_telefon', email: 'werkstatt_email' } },
];

export function BeteiligteSection({ akte }: SectionProps) {
  const staticRows = ROLE_META
    .map((r) => {
      const name = akte[r.fields.name];
      if (!name) return null;
      const ort = [r.fields.plz ? akte[r.fields.plz] : null, r.fields.ort ? akte[r.fields.ort] : null].filter(Boolean).join(' ');
      const strasse = r.fields.strasse ? akte[r.fields.strasse] : null;
      return {
        code: r.code,
        label: r.label,
        bg: r.bg,
        text: r.text,
        name,
        address: [strasse, ort].filter(Boolean).join(', '),
        telefon: r.fields.telefon ? akte[r.fields.telefon] : undefined,
        email: r.fields.email ? akte[r.fields.email] : undefined,
        versNr: r.code === 'VN' ? (akte.versicherungsnummer ?? undefined) : undefined,
      };
    })
    .filter(Boolean) as Array<{ code: string; label: string; bg: string; text: string; name: string; address: string; telefon?: string; email?: string; versNr?: string }>;

  const totalCount = staticRows.length;

  return (
    <div className="space-y-4">
      <Card>
        <SectionHeader
          icon={Users}
          title="Beteiligte"
          hint={`${totalCount} Beteiligte in dieser Akte`}
          action={<SyncBadge akte={akte} />}
        />
        {totalCount === 0 ? (
          <EmptyHint text="Noch keine Beteiligten erfasst. Daten werden beim WinValue-Sync automatisch ergänzt." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-gray-200 text-[11px] text-gray-500 font-semibold uppercase tracking-wide">
                  <th className="text-left py-2 px-2 w-20">Rolle</th>
                  <th className="text-left py-2 px-2">Name / Firma</th>
                  <th className="text-left py-2 px-2">Adresse</th>
                  <th className="text-left py-2 px-2">Kontakt</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {staticRows.map((p) => (
                  <tr key={p.code} className="align-top">
                    <td className="py-3 px-2">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex items-center justify-center text-[10px] font-bold px-2 py-0.5 rounded ${p.bg} ${p.text} w-fit`}>
                          {p.code}
                        </span>
                        <span className="text-[11px] text-gray-500">{p.label}</span>
                      </div>
                    </td>
                    <td className="py-3 px-2">
                      <p className="font-semibold text-gray-900">{p.name}</p>
                      {p.versNr && <p className="text-[11px] text-gray-500 font-mono mt-0.5">Versicherungs-Nr: {p.versNr}</p>}
                    </td>
                    <td className="py-3 px-2 text-gray-600">
                      {p.address ? (
                        <span className="inline-flex items-start gap-1.5">
                          <MapPin className="w-3 h-3 mt-1 text-gray-400 flex-shrink-0" />
                          <span>{p.address}</span>
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-3 px-2">
                      <div className="flex flex-col gap-1">
                        {p.telefon && (
                          <a href={`tel:${p.telefon}`} className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 text-[12px]">
                            <Phone className="w-3 h-3" /> {p.telefon}
                          </a>
                        )}
                        {p.email && (
                          <a href={`mailto:${p.email}`} className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 text-[12px] truncate max-w-[200px]">
                            <Mail className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{p.email}</span>
                          </a>
                        )}
                        {!p.telefon && !p.email && <span className="text-gray-400 text-[12px]">—</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 3) FAHRZEUG — Vollständige Fahrzeug-Identifikation (VIN, HSN/TSN, Laufleistung)
// ═══════════════════════════════════════════════════════════════════════════

const FAHRZEUG_ID_KEYS = [
  'kennzeichen', 'fahrzeug_vin', 'fahrzeug_marke', 'fahrzeug_modell',
  'fahrzeug_hsn', 'fahrzeug_tsn', 'fahrzeug_bauart', 'fahrzeug_farbe',
];

const FAHRZEUG_TECH_KEYS = [
  'fahrzeug_ez', 'fahrzeug_baujahr', 'fahrzeug_km', 'fahrzeug_hu',
  'fahrzeug_kw', 'fahrzeug_ccm', 'fahrzeug_klasse', 'fahrzeug_standort',
];

const FAHRZEUG_STATUS_KEYS = [
  'fahrzeug_fahrbereit', 'fahrzeug_totalschaden',
];

const ALL_FAHRZEUG_KEYS = [...FAHRZEUG_ID_KEYS, ...FAHRZEUG_TECH_KEYS, ...FAHRZEUG_STATUS_KEYS];

export function FahrzeugSection({ akte, onChanged }: SectionProps) {
  const hasData = !!(akte.fahrzeug_marke || akte.fahrzeug_vin || akte.kennzeichen);
  const leistungPS = akte.fahrzeug_kw ? Math.round(Number(akte.fahrzeug_kw) * 1.36) : null;
  const e = useEditableSection(akte, ALL_FAHRZEUG_KEYS, onChanged);

  return (
    <div className="space-y-4">
      {/* Identifikation */}
      <Card>
        <SectionHeader icon={Car} title="Identifikation" hint="Fahrzeug- und Schlüsselnummern"
          action={<div className="flex items-center gap-2"><SyncBadge akte={akte} /><EditButton editing={e.editing} saving={e.saving} onEdit={e.startEditing} onSave={e.save} onCancel={e.cancel} /></div>} />
        {!hasData && !e.editing && <EmptyHint text="Noch keine Fahrzeugdaten erfasst. Wird bei WinValue-Sync automatisch ergänzt." />}
        {e.editing ? (
          <FieldGrid cols={2}>
            <EditField label="Kennzeichen" value={e.form.kennzeichen} onChange={v => e.set('kennzeichen', v)} />
            <EditField label="FIN" value={e.form.fahrzeug_vin} onChange={v => e.set('fahrzeug_vin', v)} />
            <EditField label="Hersteller" value={e.form.fahrzeug_marke} onChange={v => e.set('fahrzeug_marke', v)} />
            <EditField label="Modell" value={e.form.fahrzeug_modell} onChange={v => e.set('fahrzeug_modell', v)} />
            <EditField label="HSN" value={e.form.fahrzeug_hsn} onChange={v => e.set('fahrzeug_hsn', v)} />
            <EditField label="TSN" value={e.form.fahrzeug_tsn} onChange={v => e.set('fahrzeug_tsn', v)} />
            <EditField label="Bauart" value={e.form.fahrzeug_bauart} onChange={v => e.set('fahrzeug_bauart', v)} />
            <EditField label="Farbe" value={e.form.fahrzeug_farbe} onChange={v => e.set('fahrzeug_farbe', v)} />
          </FieldGrid>
        ) : (
          <FieldGrid cols={2}>
            <InfoRow label="Kennzeichen" value={akte.kennzeichen || akte.fahrzeug_kennzeichen} mono />
            <InfoRow label="FIN" value={akte.fahrzeug_vin || akte.fin} mono />
            <InfoRow label="Hersteller" value={akte.fahrzeug_marke} />
            <InfoRow label="Modell" value={[akte.fahrzeug_modell, akte.fahrzeug_submodell].filter(Boolean).join(' ')} />
            <InfoRow label="HSN" value={akte.fahrzeug_hsn} mono />
            <InfoRow label="TSN" value={akte.fahrzeug_tsn} mono />
            <InfoRow label="Bauart" value={akte.fahrzeug_bauart} />
            <InfoRow label="Farbe" value={akte.fahrzeug_farbe} />
          </FieldGrid>
        )}
      </Card>

      {/* Technik */}
      <Card>
        <SectionHeader icon={Gauge} title="Technische Daten" hint="Leistung, Hubraum, Erstzulassung" />
        {e.editing ? (
          <FieldGrid cols={2}>
            <EditField label="Erstzulassung" value={e.form.fahrzeug_ez} type="date" onChange={v => e.set('fahrzeug_ez', v)} />
            <EditField label="Baujahr" value={e.form.fahrzeug_baujahr} onChange={v => e.set('fahrzeug_baujahr', v)} />
            <EditField label="Laufleistung (km)" value={e.form.fahrzeug_km} onChange={v => e.set('fahrzeug_km', v)} />
            <EditField label="Nächste HU" value={e.form.fahrzeug_hu} type="date" onChange={v => e.set('fahrzeug_hu', v)} />
            <EditField label="Leistung (kW)" value={e.form.fahrzeug_kw} onChange={v => e.set('fahrzeug_kw', v)} />
            <EditField label="Hubraum (ccm)" value={e.form.fahrzeug_ccm} onChange={v => e.set('fahrzeug_ccm', v)} />
            <EditField label="Klasse" value={e.form.fahrzeug_klasse} onChange={v => e.set('fahrzeug_klasse', v)} />
            <EditField label="Standort" value={e.form.fahrzeug_standort} onChange={v => e.set('fahrzeug_standort', v)} />
          </FieldGrid>
        ) : (
          <FieldGrid cols={2}>
            <InfoRow label="Erstzulassung" value={formatDate(akte.fahrzeug_ez)} />
            <InfoRow label="Baujahr" value={akte.fahrzeug_baujahr} />
            <InfoRow label="Laufleistung" value={akte.fahrzeug_km != null ? `${Number(akte.fahrzeug_km).toLocaleString('de-DE')} km` : null} />
            <InfoRow label="Nächste HU" value={formatDate(akte.fahrzeug_hu)} />
            <InfoRow label="Leistung" value={akte.fahrzeug_kw != null ? `${akte.fahrzeug_kw} kW${leistungPS ? ` (${leistungPS} PS)` : ''}` : null} />
            <InfoRow label="Hubraum" value={akte.fahrzeug_ccm != null ? `${Number(akte.fahrzeug_ccm).toLocaleString('de-DE')} ccm` : null} />
            <InfoRow label="Klasse" value={akte.fahrzeug_klasse} />
            <InfoRow label="Standort" value={akte.fahrzeug_standort} />
          </FieldGrid>
        )}
      </Card>

      {/* Fahrzeugstatus */}
      <Card>
        <SectionHeader icon={Shield} title="Fahrzeugstatus" hint="Fahrbereitschaft und Totalschaden" />
        {e.editing ? (
          <div className="flex flex-wrap gap-4">
            {([
              { key: 'fahrzeug_fahrbereit', label: 'Fahrbereit' },
              { key: 'fahrzeug_totalschaden', label: 'Totalschaden' },
            ] as const).map(item => (
              <label key={item.key} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={e.form[item.key] === 'true' || e.form[item.key] === true as any}
                  onChange={ev => e.set(item.key, ev.target.checked ? 'true' : '')}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-[13px] text-gray-700">{item.label}</span>
              </label>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <StatusChip active={!!akte.fahrzeug_fahrbereit} label="Fahrbereit" activeColor="emerald" />
            <StatusChip active={!!akte.fahrzeug_totalschaden} label="Totalschaden" activeColor="red" />
            <StatusChip active={!!akte.fahrzeug_verkehrssicher} label="Verkehrssicher" activeColor="emerald" />
            <StatusChip active={!!akte.notreparatur_moeglich} label="Notreparatur möglich" activeColor="amber" />
          </div>
        )}
      </Card>
    </div>
  );
}

function StatusChip({ active, label, activeColor }: { active: boolean; label: string; activeColor: 'emerald' | 'red' | 'amber' }) {
  const bgOn = activeColor === 'emerald' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : activeColor === 'red' ? 'bg-red-50 text-red-700 border-red-200'
    : 'bg-amber-50 text-amber-700 border-amber-200';
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[12px] font-medium ${active ? bgOn : 'bg-gray-50 text-gray-400 border-gray-200'}`}>
      {active ? <CheckCircle2 className="w-3.5 h-3.5" /> : <span className="w-3.5 h-3.5 rounded-full border border-current" />}
      {label}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 4) BESICHTIGUNGEN — Alle Termine zur Fahrzeugbesichtigung
// ═══════════════════════════════════════════════════════════════════════════

export function BesichtigungenSection({ akte }: SectionProps) {
  return (
    <div className="space-y-4">
      <Card>
        <SectionHeader icon={Eye} title="Besichtigungen" hint="Termine zur Fahrzeugaufnahme" action={<SyncBadge akte={akte} />} />
        {akte.besichtigungsdatum ? (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-600 text-white flex flex-col items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-semibold uppercase leading-none">
                  {new Date(akte.besichtigungsdatum).toLocaleDateString('de-DE', { month: 'short' })}
                </span>
                <span className="text-[16px] font-bold leading-none mt-0.5">
                  {new Date(akte.besichtigungsdatum).getDate()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-semibold text-gray-900">Erstbesichtigung</p>
                <p className="text-[12px] text-gray-600 mt-0.5">{formatDate(akte.besichtigungsdatum)}</p>
                {akte.schadenort && (
                  <p className="text-[12px] text-gray-600 mt-1 inline-flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {akte.schadenort}
                  </p>
                )}
                <p className="text-[12px] text-gray-500 mt-2">Besichtigt durch {akte.besichtigt_durch || 'Andreas Koop'}</p>
              </div>
            </div>
          </div>
        ) : (
          <EmptyHint text="Kein Besichtigungstermin erfasst." />
        )}
      </Card>

      {/* Fahrzeug-Zustandsvermerk (Platzhalter — noch nicht in DB) */}
      <Card>
        <SectionHeader icon={Camera} title="Zustand bei Besichtigung" hint="Siehe Lichtbilder-Sektion für Fotos" />
        <EmptyHint text="Foto-Dokumentation siehe Lichtbilder-Schritt. PDF-Befundbericht erscheint im Dokumente-Ordner." />
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 5) LICHTBILDER — Alle Fahrzeug-Fotos (aus Storage)
// ═══════════════════════════════════════════════════════════════════════════

type SortMode = 'newest' | 'oldest' | 'name';

export function LichtbilderSection({ akte, onChanged }: SectionProps) {
  const akteId = akte.id;
  const [docs, setDocs] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [sortMode, setSortMode] = useState<SortMode>('newest');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from('dokumente')
        .select('id,dateiname,dateipfad,mime_type,dateigroesse,kategorie,dokument_typ,titel,erstellt_am')
        .eq('akte_id', akteId)
        .in('kategorie', ['lichtbilder', 'fotos']);
      if (!cancelled) { setDocs(data || []); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [akteId, reloadKey]);

  const sortDocs = (items: any[]): any[] => {
    const sorted = [...items];
    switch (sortMode) {
      case 'newest': return sorted.sort((a, b) => (b.erstellt_am || '').localeCompare(a.erstellt_am || ''));
      case 'oldest': return sorted.sort((a, b) => (a.erstellt_am || '').localeCompare(b.erstellt_am || ''));
      case 'name': return sorted.sort((a, b) => (a.dateiname || '').localeCompare(b.dateiname || ''));
      default: return sorted;
    }
  };

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let uploaded = 0;
    let errors = 0;
    try {
      for (const file of Array.from(files)) {
        const ext = file.name.split('.').pop() || '';
        const storagePath = `akten/${akteId}/lichtbilder/${Date.now()}_${file.name}`;
        const { error: uploadError } = await supabase.storage
          .from('dokumente')
          .upload(storagePath, file, { contentType: file.type });
        if (uploadError) { errors++; continue; }
        const { error: dbError } = await supabase.from('dokumente').insert({
          akte_id: akteId,
          dateiname: file.name,
          dateipfad: storagePath,
          mime_type: file.type,
          dateigroesse: file.size,
          kategorie: 'lichtbilder',
          dokument_typ: ext.toUpperCase(),
          titel: file.name.replace(/\.[^.]+$/, ''),
        });
        if (dbError) { errors++; } else { uploaded++; }
      }
      if (uploaded > 0) toast.success(`${uploaded} Lichtbild${uploaded > 1 ? 'er' : ''} hochgeladen`);
      if (errors > 0) toast.error(`${errors} Fehler beim Upload`);
      setReloadKey(k => k + 1);
      onChanged?.();
    } catch (err: any) {
      toast.error('Upload fehlgeschlagen', { description: err.message });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDrop = (ev: React.DragEvent) => {
    ev.preventDefault();
    setDragOver(false);
    handleUpload(ev.dataTransfer.files);
  };

  const images = sortDocs((docs || []).filter((d) => (d.mime_type || '').startsWith('image/')));
  const pdfs = (docs || []).filter((d) => (d.mime_type || '').includes('pdf'));
  const zips = (docs || []).filter((d) => (d.mime_type || '').includes('zip'));

  return (
    <div className="space-y-4">
      <Card>
        <SectionHeader
          icon={Camera}
          title="Lichtbilder"
          hint={loading ? 'Lade Fotos...' : `${images.length} Fotos, ${pdfs.length} PDF, ${zips.length} ZIP`}
          action={<SyncBadge akte={akte} />}
        />

        {/* Upload-Zone */}
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors mb-4 ${
            dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
          }`}
          onDragOver={ev => { ev.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf,.zip" className="hidden"
            onChange={ev => handleUpload(ev.target.files)} />
          {uploading ? (
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
              <span className="text-[13px] text-blue-600 font-medium">Wird hochgeladen...</span>
            </div>
          ) : (
            <>
              <Upload className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-[13px] text-gray-500">
                Dateien hierher ziehen oder{' '}
                <button onClick={() => fileInputRef.current?.click()} className="text-blue-600 hover:text-blue-800 font-medium underline">
                  durchsuchen
                </button>
              </p>
              <p className="text-[11px] text-gray-400 mt-1">Bilder, PDF und ZIP erlaubt</p>
            </>
          )}
        </div>

        {/* Sort-Controls */}
        {images.length > 1 && (
          <div className="flex items-center gap-2 mb-3">
            <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-[11px] text-gray-400">Sortierung:</span>
            {([
              { mode: 'newest' as SortMode, label: 'Neueste', icon: SortDesc },
              { mode: 'oldest' as SortMode, label: 'Älteste', icon: SortAsc },
              { mode: 'name' as SortMode, label: 'Name', icon: FileText },
            ]).map(s => (
              <button key={s.mode} onClick={() => setSortMode(s.mode)}
                className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-colors ${
                  sortMode === s.mode ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'
                }`}>
                <s.icon className="w-3 h-3" /> {s.label}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
        ) : (docs || []).length === 0 ? (
          <div className="space-y-3">
            <EmptyHint text='Noch keine Lichtbilder. Laden Sie Fotos hoch oder nutzen Sie "Dokumente von WinValue laden".' />
            {akte.winvalue_uid && <DownloadFromWinValueButton akte={akte} />}
          </div>
        ) : (
          <>
            {images.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {images.map((d) => <ImageCard key={d.id} doc={d} />)}
              </div>
            )}
            {(pdfs.length > 0 || zips.length > 0) && (
              <div className="mt-4 space-y-2">
                {[...pdfs, ...zips].map((d) => <DocDownloadRow key={d.id} doc={d} />)}
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

function ImageCard({ doc }: { doc: any }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.storage.from('dokumente').createSignedUrl(doc.dateipfad, 3600);
      if (!cancelled) setUrl(data?.signedUrl || null);
    })();
    return () => { cancelled = true; };
  }, [doc.dateipfad]);
  return (
    <a
      href={url || '#'}
      target="_blank"
      rel="noopener noreferrer"
      className="group block relative aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200 hover:border-blue-400 transition-colors"
    >
      {url ? (
        <img src={url} alt={doc.titel || doc.dateiname} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-gray-400">
          <ImageIcon className="w-6 h-6" />
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 p-1.5 bg-gradient-to-t from-black/60 to-transparent">
        <p className="text-[10px] text-white truncate">{doc.titel || doc.dateiname}</p>
      </div>
    </a>
  );
}

function DocDownloadRow({ doc }: { doc: any }) {
  const openDoc = async () => {
    const { data, error } = await supabase.storage.from('dokumente').createSignedUrl(doc.dateipfad, 3600);
    if (error || !data?.signedUrl) { toast.error('Konnte Dokument nicht öffnen'); return; }
    window.open(data.signedUrl, '_blank', 'noopener');
  };
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-blue-300 bg-white transition-colors">
      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
        <FileText className="w-4 h-4 text-blue-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <p className="text-[13px] font-semibold text-gray-900 truncate">{doc.titel || doc.dateiname}</p>
          {hasStellungnahmeLabel(doc.tags) && <StellungnahmeBadge />}
        </div>
        <p className="text-[11px] text-gray-500">{doc.dokument_typ} · {formatBytes(doc.dateigroesse)}</p>
      </div>
      <button onClick={openDoc} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-[11px] font-medium hover:bg-blue-700 transition-colors">
        <Download className="w-3.5 h-3.5" /> Öffnen
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 6) BEWERTUNG — Reparaturkosten & Wiederbeschaffungswert
// ═══════════════════════════════════════════════════════════════════════════

export function BewertungSection({ akte }: SectionProps) {
  const rep = akte.reparaturkosten_brutto || akte.reparaturkosten_netto;
  const wbw = akte.wiederbeschaffungswert;
  const minderwert = akte.minderwert;

  // Wirtschaftlich Totalschaden wenn Reparaturkosten > WBW
  const totalschaden = rep != null && wbw != null && Number(rep) > Number(wbw);

  return (
    <div className="space-y-4">
      <Card>
        <SectionHeader icon={BarChart3} title="Bewertung" hint="Kosten, Werte und Schadenshöhe" action={<SyncBadge akte={akte} />} />

        {/* Hauptwerte */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <CostValue label="Reparaturkosten (brutto)" value={akte.reparaturkosten_brutto} highlight />
          <CostValue label="Wiederbeschaffungswert" value={wbw} highlight />
          <CostValue label="Merkantile Wertminderung" value={minderwert} />
        </div>

        {/* Detailkosten */}
        <div className="border-t border-gray-100 pt-4">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Details (netto)</p>
          <FieldGrid cols={2}>
            <InfoRow label="Reparaturkosten netto" value={formatEuro(akte.reparaturkosten_netto)} mono />
            <InfoRow label="WBW netto" value={formatEuro(akte.wiederbeschaffungswert_netto)} mono />
            <InfoRow label="MwSt-Satz" value={akte.mwst_satz != null ? `${akte.mwst_satz} %` : null} />
            <InfoRow label="Nutzungsausfall/Tag" value={formatEuro(akte.nutzungsausfall_betrag)} mono />
            <InfoRow label="Ersatzteilkosten" value={formatEuro(akte.ersatzteilkosten_netto)} mono />
            <InfoRow label="Lohnkosten" value={formatEuro(akte.lohnkosten_netto)} mono />
            <InfoRow label="Lackkosten" value={formatEuro(akte.lackkosten_netto)} mono />
            <InfoRow label="Zusatzkosten" value={formatEuro(akte.zusatzkosten_netto)} mono />
          </FieldGrid>
        </div>

        {totalschaden && (
          <div className="mt-4 rounded-lg border-2 border-red-300 bg-red-50 p-3 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <p className="text-[13px] font-bold text-red-800">Wirtschaftlicher Totalschaden</p>
              <p className="text-[11px] text-red-700">Reparaturkosten übersteigen den Wiederbeschaffungswert.</p>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 7) RESTWERT — Restwert / Abzüge
// ═══════════════════════════════════════════════════════════════════════════

export function RestwertSection({ akte }: SectionProps) {
  const rw = akte.restwert ?? akte.wiederbeschaffungswert_netto ? null : null;
  return (
    <div className="space-y-4">
      <Card>
        <SectionHeader icon={TrendingDown} title="Restwert" hint="Wert des beschädigten Fahrzeugs" action={<SyncBadge akte={akte} />} />
        <FieldGrid cols={2}>
          <InfoRow label="Restwert" value={formatEuro(akte.restwert)} mono />
          <InfoRow label="Wiederbeschaffungswert" value={formatEuro(akte.wiederbeschaffungswert)} mono />
        </FieldGrid>
        {akte.restwert == null && (
          <div className="mt-3">
            <EmptyHint text="Noch kein Restwert erfasst. In WinValue unter Restwert eingeben und synchronisieren." />
          </div>
        )}
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 8) MINDERWERT — Merkantile Wertminderung
// ═══════════════════════════════════════════════════════════════════════════

export function MinderwertSection({ akte }: SectionProps) {
  return (
    <div className="space-y-4">
      <Card>
        <SectionHeader icon={ArrowDownRight} title="Merkantile Wertminderung" hint="Wertverlust nach Reparatur" action={<SyncBadge akte={akte} />} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <CostValue label="Merkantile Wertminderung" value={akte.minderwert} highlight />
          <CostValue label="Wiederbeschaffungswert" value={akte.wiederbeschaffungswert} />
        </div>
        <div className="mt-4">
          <p className="text-[11px] text-gray-500 leading-relaxed">
            Die merkantile Wertminderung erfasst den Minderwert, der trotz fachgerechter Reparatur
            durch das Unfallereignis entsteht (§ 287 ZPO, BGH-Rechtsprechung).
          </p>
        </div>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 9) RECHNUNG — Sachverständigen-Rechnung
// ═══════════════════════════════════════════════════════════════════════════

function SvRechnungButton({ akteId }: { akteId: string }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ rechnungsNr?: string; bruttoGesamt?: number } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  async function erstelleRechnung() {
    setLoading(true);
    setResult(null);
    setFehler(null);
    try {
      const res = await fetch('/api/sv-rechnung', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ akteId }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 403 && data.hint) {
          setFehler(`Feature nicht aktiv: ${data.hint}`);
        } else {
          setFehler(data.error || 'Unbekannter Fehler');
        }
        toast.error('Rechnung konnte nicht erstellt werden', { description: data.error });
      } else {
        setResult({ rechnungsNr: data.rechnungsNr, bruttoGesamt: data.bruttoGesamt });
        toast.success(`Rechnung ${data.rechnungsNr} erstellt`, {
          description: `Brutto: ${new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(data.bruttoGesamt)}`,
        });
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Netzwerkfehler';
      setFehler(msg);
      toast.error('Rechnung fehlgeschlagen', { description: msg });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mt-4 space-y-2">
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={erstelleRechnung}
          disabled={loading || !!result}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
          {loading ? 'Rechnung wird erstellt…' : 'Rechnung in unserem System erstellen (Beta)'}
        </button>
        {result && (
          <span className="text-[12px] text-green-700 font-medium flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" />
            {result.rechnungsNr} erstellt
          </span>
        )}
      </div>
      {fehler && (
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          {fehler}
        </p>
      )}
    </div>
  );
}

export function RechnungSection({ akte }: SectionProps) {
  return (
    <div className="space-y-4">
      <Card>
        <SectionHeader icon={Receipt} title="Rechnung" hint="Sachverständigen-Rechnung an Versicherung" action={<SyncBadge akte={akte} />} />
        <FieldGrid cols={2}>
          <InfoRow label="Gesamtbetrag" value={formatEuro(akte.gesamtbetrag)} mono />
          <InfoRow label="Schadennummer" value={akte.schadennummer} mono />
          <InfoRow label="Versicherung" value={akte.versicherung} />
          <InfoRow label="Versicherungs-Nr" value={akte.versicherungsnummer} mono />
          <InfoRow label="Rechnungsdatum" value={formatDate(akte.rechnungsdatum)} />
          <InfoRow label="Status" value={akte.rechnung_status || '—'} />
        </FieldGrid>

        <div className="mt-4">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Kostenpositionen</p>
          <FieldGrid cols={2}>
            <InfoRow label="SV-Honorar netto" value={formatEuro(akte.sv_honorar_netto)} mono />
            <InfoRow label="Nebenkosten" value={formatEuro(akte.nebenkosten)} mono />
            <InfoRow label="Auslagen" value={formatEuro(akte.auslagen)} mono />
            <InfoRow label="MwSt" value={akte.mwst_satz != null ? `${akte.mwst_satz} %` : null} />
          </FieldGrid>
        </div>

        {(!akte.gesamtbetrag && !akte.sv_honorar_netto) && (
          <div className="mt-4">
            <EmptyHint text="Rechnung wird nach WinValue-Sync angezeigt. Original-Rechnung PDF ist im Dokumente-Tab verfügbar." />
          </div>
        )}

        <SvRechnungButton akteId={akte.id} />
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 10) PROTOKOLL — Versand-/Aktionsprotokoll
// ═══════════════════════════════════════════════════════════════════════════

export function ProtokollSection({ akte }: SectionProps) {
  const akteId = akte.id;
  const [events, setEvents] = useState<any[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Aufgaben + Dokument-Uploads als Events
      const [{ data: docs }, { data: aufgaben }] = await Promise.all([
        supabase.from('dokumente').select('id,dateiname,kategorie,erstellt_am').eq('akte_id', akteId).order('erstellt_am', { ascending: false }).limit(50),
        supabase.from('aufgaben').select('id,titel,status,erstellt_am,faelligkeit').eq('akte_id', akteId).order('erstellt_am', { ascending: false }).limit(50),
      ]);
      if (cancelled) return;
      const evs: any[] = [];
      (docs || []).forEach((d) => evs.push({ typ: 'dokument', titel: `Dokument: ${d.dateiname}`, kategorie: d.kategorie, zeit: d.erstellt_am }));
      (aufgaben || []).forEach((a) => evs.push({ typ: 'aufgabe', titel: `Aufgabe: ${a.titel}`, status: a.status, zeit: a.erstellt_am }));
      evs.push({ typ: 'akte', titel: 'Akte erstellt', zeit: akte.created_at });
      if (akte.winvalue_last_sync) evs.push({ typ: 'sync', titel: 'WinValue synchronisiert', zeit: akte.winvalue_last_sync });
      evs.sort((a, b) => (b.zeit || '').localeCompare(a.zeit || ''));
      setEvents(evs);
    })();
    return () => { cancelled = true; };
  }, [akteId, akte.created_at, akte.winvalue_last_sync]);

  return (
    <div className="space-y-4">
      <Card>
        <SectionHeader icon={ScrollText} title="Aktenprotokoll" hint="Chronologische Aktivitäten" action={<SyncBadge akte={akte} />} />
        {!events ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
        ) : events.length === 0 ? (
          <EmptyHint text="Noch keine Aktivitäten protokolliert." />
        ) : (
          <ol className="relative border-l-2 border-gray-200 ml-3 space-y-4">
            {events.map((e, i) => (
              <li key={i} className="ml-6">
                <span className="absolute -left-[9px] w-4 h-4 rounded-full bg-white border-2 border-blue-500" />
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-medium text-gray-800">{e.titel}</p>
                    {e.kategorie && <p className="text-[11px] text-gray-500">Kategorie: {e.kategorie}</p>}
                    {e.status && <p className="text-[11px] text-gray-500">Status: {e.status}</p>}
                  </div>
                  <time className="text-[11px] text-gray-400 flex-shrink-0 whitespace-nowrap">
                    {e.zeit ? new Date(e.zeit).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </time>
                </div>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 11) DOKUMENTE — WinValue-Download-Button (wird in Dokumente-Section integriert)
// ═══════════════════════════════════════════════════════════════════════════

export function DownloadFromWinValueButton({ akte, onDone }: { akte: Akte; onDone?: () => void }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ downloaded?: number; skipped?: number; errors?: number; total?: number } | null>(null);

  async function trigger() {
    if (!akte.winvalue_uid) { toast.error('Keine WinValue-UID vorhanden'); return; }
    setLoading(true);
    setResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/winvalue-sync?action=pull-docs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ akteId: akte.id, winvalueUid: akte.winvalue_uid }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error('Download fehlgeschlagen', { description: data.error || 'Serverfehler' });
      } else {
        setResult(data);
        const msg = `${data.downloaded || 0} neu, ${data.skipped || 0} vorhanden${data.errors ? `, ${data.errors} Fehler` : ''}`;
        if ((data.downloaded || 0) + (data.skipped || 0) > 0) {
          toast.success(`Dokumente geladen: ${msg}`);
        } else {
          toast.info('Keine Dokumente gefunden');
        }
        onDone?.();
      }
    } catch (e: any) {
      toast.error('Download fehlgeschlagen', { description: e.message });
    } finally {
      setLoading(false);
    }
  }

  if (!akte.winvalue_uid) return null;

  return (
    <div className="flex flex-wrap items-center gap-3">
      <button
        onClick={trigger}
        disabled={loading}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        {loading ? 'Lade Dokumente...' : 'Dokumente von WinValue laden'}
      </button>
      {result && (
        <span className="text-[12px] text-gray-500">
          {result.downloaded || 0} neu · {result.skipped || 0} bereits vorhanden · {result.total || 0} gesamt
          {(result.errors || 0) > 0 && <span className="text-red-600"> · {result.errors} Fehler</span>}
        </span>
      )}
      {akte.winvalue_url && (
        <a
          href={akte.winvalue_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[12px] text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
        >
          <ExternalLink className="w-3.5 h-3.5" /> In WinValue öffnen
        </a>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DokumenteList — Liste aller Dokumente (gruppiert nach Kategorie)
// Wird im Dokumente-Tab zusätzlich zum vorhandenen AktenOrdner angezeigt.
// ═══════════════════════════════════════════════════════════════════════════

export function WinValueDokumenteList({ akte, reloadKey }: { akte: Akte; reloadKey?: number }) {
  const [docs, setDocs] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from('dokumente')
        .select('id,dateiname,dateipfad,mime_type,dateigroesse,kategorie,dokument_typ,titel,erstellt_am,tags')
        .eq('akte_id', akte.id)
        .not('kategorie', 'in', '(lichtbilder,fotos)')
        .order('erstellt_am', { ascending: false });
      if (!cancelled) { setDocs(data || []); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [akte.id, reloadKey]);

  if (loading) {
    return <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;
  }

  if (!docs || docs.length === 0) {
    return null;
  }

  // Gruppieren nach Kategorie
  const groups = docs.reduce((acc: Record<string, any[]>, d) => {
    const k = d.kategorie || 'sonstige';
    (acc[k] = acc[k] || []).push(d);
    return acc;
  }, {});

  const order = ['gutachten', 'kalkulation', 'lichtbilder', 'bewertung', 'rechnung', 'auftrag', 'sonstige'];
  const keys = Object.keys(groups).sort((a, b) => {
    const ia = order.indexOf(a); const ib = order.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });

  const KATEGORIE_LABELS: Record<string, string> = {
    gutachten: 'Gutachten',
    kalkulation: 'Kalkulation',
    lichtbilder: 'Lichtbilder',
    bewertung: 'Bewertung',
    rechnung: 'Rechnung',
    auftrag: 'Auftrag',
    sonstige: 'Sonstige',
  };

  return (
    <div className="space-y-4">
      {keys.map((k) => (
        <div key={k}>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
            {KATEGORIE_LABELS[k] || k} <span className="text-gray-400 font-normal">· {groups[k].length}</span>
          </p>
          <div className="space-y-2">
            {groups[k].map((d: any) => <DocDownloadRow key={d.id} doc={d} />)}
          </div>
        </div>
      ))}
    </div>
  );
}
